import { Anchor, ScrollArea, Table, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import type { DashboardReportMeta, DashboardReportRun } from "../../../types/api";
import { formatDate, formatPaise, STATUS_LABELS } from "../../../lib/format";
import { StatusBadge } from "../../StatusBadge";
import { SortableTh, useTableSort, type SortValue } from "../../ui";

interface TableWidgetProps {
  report: DashboardReportMeta;
  run: DashboardReportRun;
}

interface RowLinkConfig {
  id_column: string;
  path_prefix: string;
}

function formatCell(column: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (column.endsWith("_paise") || column === "net_paise") return formatPaise(Number(value));
  if (column.endsWith("_at") || column === "updated_at" || column === "created_at" || column === "submitted_at") {
    return formatDate(String(value));
  }
  if (column === "status" && typeof value === "string") {
    return STATUS_LABELS[value] ?? value.replace(/_/g, " ");
  }
  if (typeof value === "number") return value.toLocaleString("en-IN");
  return String(value);
}

function columnLabel(column: string): string {
  return column
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function sortValueForColumn(column: string, value: unknown): SortValue {
  if (value === null || value === undefined) return null;
  if (column.endsWith("_paise") || column === "net_paise") return Number(value);
  if (column.endsWith("_at") || column === "updated_at" || column === "created_at" || column === "submitted_at") {
    return new Date(String(value));
  }
  if (column === "status" && typeof value === "string") {
    return STATUS_LABELS[value] ?? value;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  return String(value);
}

export function TableWidget({ report, run }: TableWidgetProps) {
  const config = report.config_json;
  const columns = (run.data.columns as string[]) ?? [];
  const hidden = new Set((config.hidden_columns as string[] | undefined) ?? []);
  const visibleColumns = columns.filter((col) => !hidden.has(col));
  const rows = (run.data.rows as Array<Record<string, unknown>>) ?? [];
  const rowLink = config.row_link as RowLinkConfig | undefined;

  const accessors: Record<string, (row: Record<string, unknown>) => SortValue> = {};
  for (const col of visibleColumns) {
    accessors[col] = (row) => sortValueForColumn(col, row[col]);
  }
  const { sorted: sortedRows, sortKey, direction, toggle } = useTableSort(rows, accessors);

  if (visibleColumns.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No data
      </Text>
    );
  }

  return (
    <ScrollArea className="dashboard-table-host" type="auto">
      <Table striped highlightOnHover verticalSpacing="md" fz="sm">
        <Table.Thead>
          <Table.Tr>
            {visibleColumns.map((col) => (
              <SortableTh key={col} columnKey={col} sortKey={sortKey} direction={direction} onSort={toggle}>
                {columnLabel(col)}
              </SortableTh>
            ))}
            {rowLink && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={visibleColumns.length + (rowLink ? 1 : 0)}>
                <Text size="sm" c="dimmed">
                  No rows
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            sortedRows.map((row, index) => {
              const linkId = rowLink ? row[rowLink.id_column] : null;
              const href =
                linkId && rowLink ? `${rowLink.path_prefix}${String(linkId)}` : null;

              return (
                <Table.Tr key={index}>
                  {visibleColumns.map((col) => (
                    <Table.Td key={col}>
                      {col === "status" && typeof row[col] === "string" ? (
                        <StatusBadge status={row[col] as string} />
                      ) : (
                        formatCell(col, row[col])
                      )}
                    </Table.Td>
                  ))}
                  {rowLink && href && (
                    <Table.Td>
                      <Anchor component={Link} to={href} size="sm">
                        View
                      </Anchor>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}
